import type { FieldId } from './result'
import type { FormDefinition } from '@/core/templates/types'
import { getAt } from './path'
import { isValidGermanDate } from './germanDate'

export type PresenceRules = {
  required: FieldId[]
  oneOf?: FieldId[][]
}

export type PresenceResult = {
  missing: FieldId[]
  unsatisfiedGroups: FieldId[][]
}

const isEmpty = (v: unknown) => v == null || String(v).trim() === ''

export function validatePresence(values: unknown, rules: PresenceRules): PresenceResult {
  const missing: FieldId[] = []
  for (const path of rules.required) {
    if (isEmpty(getAt(values, path))) missing.push(path)
  }
  const unsatisfiedGroups: FieldId[][] = []
  for (const group of rules.oneOf ?? []) {
    if (group.every((p) => isEmpty(getAt(values, p)))) unsatisfiedGroups.push(group)
  }
  return { missing, unsatisfiedGroups }
}

export const AVGS_DATE_PATHS: FieldId[] = [
  'Maßnahme.Gültigkeit Beginn',
  'Maßnahme.Gültigkeit Ende',
]
export const BEWILLIGUNG_DATE_PATHS: FieldId[] = [
  'Maßnahme.Beginn der Maßnahme',
  'Maßnahme.Ende der Maßnahme',
]

export function findMalformedDates(values: unknown, datePaths: FieldId[]): FieldId[] {
  return datePaths.filter((p) => {
    const v = getAt(values, p)
    return !isEmpty(v) && !isValidGermanDate(v)
  })
}

export const AVGS_NUMBER_PATHS: FieldId[] = [
  'Maßnahme.Zeitlicher Umfang (max UE)',
  'Teilnehmer.AVGS-Nummer (laufende Nummer)',
]

const INT4_MAX = 2_147_483_647

export function findMalformedNumbers(values: unknown, numberPaths: FieldId[]): FieldId[] {
  return numberPaths.filter((p) => {
    const v = getAt(values, p)
    if (isEmpty(v)) return false
    const t = String(v).trim()
    if (!/^\d+$/.test(t)) return true
    const n = parseInt(t, 10)
    return n === 0 || n > INT4_MAX
  })
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const PLZ_RE = /^\d{5}$/

export function findMalformedEmails(values: unknown, emailPaths: FieldId[]): FieldId[] {
  return emailPaths.filter((p) => {
    const v = getAt(values, p)
    return !isEmpty(v) && !EMAIL_RE.test(String(v).trim())
  })
}

export function findMalformedPlz(values: unknown, plzPaths: FieldId[]): FieldId[] {
  return plzPaths.filter((p) => {
    const v = getAt(values, p)
    return !isEmpty(v) && !PLZ_RE.test(String(v).trim())
  })
}

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/

export const ECN_TIME_PATHS: FieldId[] = ['Sitzung.Beginn']

export function findMalformedTimes(values: unknown, timePaths: FieldId[]): FieldId[] {
  return timePaths.filter((p) => {
    const v = getAt(values, p)
    return !isEmpty(v) && !TIME_RE.test(String(v).trim())
  })
}

export function templatePaths(template: FormDefinition): FieldId[] {
  return Object.values(template.sections).flatMap(({ fields }) => fields.map((field) => field.id))
}

export function presenceRulesFromTemplate(
  template: FormDefinition,
  opts: { optional?: FieldId[]; oneOf?: FieldId[][] } = {},
): PresenceRules {
  const exempt = new Set<FieldId>([...(opts.optional ?? []), ...(opts.oneOf ?? []).flat()])
  return {
    required: templatePaths(template).filter((p) => !exempt.has(p)),
    oneOf: opts.oneOf,
  }
}
